'use client';

import { useState } from 'react';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import { CardAppointmentCancelRequestModal } from './card-appointment-cancel-request-modal.component';

import { Appointment } from '@/types/appointments.types';
import { Icon } from '@/components/ui/icon.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { AppointmentCancelStatus } from '@/types/enums/appointments.enum';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface CardAppointmentCancelSectionProps {
  appointment: Appointment;
  showCancelButton?: boolean;
  onEditPatient?: () => void;
  onEditDate?: () => void;
}

export function CardAppointmentCancelSection({
  appointment,
  showCancelButton = true,
  onEditPatient,
  onEditDate,
}: CardAppointmentCancelSectionProps) {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const analytics = getAnalyticsPlugin();

  const handleCancelClick = () => {
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_REQUEST_CANCEL);
    setIsCancelModalOpen(true);
  };

  if (!showCancelButton) {
    return null;
  }

  return (
    <>
      {appointment.requestCancelStatus === AppointmentCancelStatus.PENDING && (
        <>
          <hr className="border-t border-gray-200 w-full" />
          <div
            className="flex items-start gap-3 bg-warning-100 border border-warning-300 rounded-2xl shadow-[0px_2px_3px_rgba(0,0,0,0.1)] px-3 py-3"
            role="alert"
            aria-label="Aviso de cancelamento pendente"
          >
            <Icon icon={faCircleExclamation} className="text-warning-600 text-2xl shrink-0" aria-hidden="true" />
            <div className="flex flex-col">
              <Typography as="span" className="text-base text-warning-800 font-bold">
                Cancelamento pendente:
              </Typography>
              <Typography as="span" className="text-base text-warning-800 font-semibold">
                Esse agendamento está em processo de cancelamento.
              </Typography>
            </div>
          </div>
        </>
      )}

      {appointment.requestCancelStatus !== AppointmentCancelStatus.PENDING && appointment.cancelable && (
        <>
          <hr className="border-t border-gray-200 w-full" />
          <button
            onClick={handleCancelClick}
            className="text-red-500 font-semibold text-lg text-center w-full block py-3 hover:text-red-600 active:text-red-700 transition-colors"
            type="button"
          >
            Solicitar cancelamento
          </button>
        </>
      )}

      {appointment.requestCancelStatus !== AppointmentCancelStatus.PENDING && !appointment.cancelable && (
        <>
          <hr className="border-t border-gray-200 w-full" />
          <div
            className="flex items-start gap-3 bg-warning-100 border border-warning-300 rounded-2xl shadow-[0px_2px_3px_rgba(0,0,0,0.1)] px-3 py-3"
            role="alert"
            aria-label="Aviso de cancelamento indisponível"
          >
            <Icon icon={faCircleExclamation} className="text-warning-600 text-2xl shrink-0" aria-hidden="true" />
            <div className="flex flex-col">
              <Typography as="span" className="text-base text-warning-800 font-bold">
                Cancelamento indisponível:
              </Typography>
              <Typography as="span" className="text-base text-warning-800 font-semibold">
                Faltam menos de 4 horas para o atendimento.
              </Typography>
            </div>
          </div>
        </>
      )}

      <CardAppointmentCancelRequestModal
        appointment={appointment}
        open={isCancelModalOpen}
        onOpenChange={setIsCancelModalOpen}
        onEditPatient={onEditPatient}
        onEditDate={onEditDate}
      />
    </>
  );
}
